/**
 * Turns the API's `accessState` into the words a page shows.
 *
 * WHY THIS EXISTS
 *
 * Pages used to decide the message from the trial dates:
 *
 *   trialStatus !== "not_started" && !isInTrial && status !== "active"
 *
 * Once the trial was over that was true for EVERY non-active subscription, so a business
 * that had paid for months and was then canceled was shown "Your trial has ended" — and,
 * on the billing page, "Your payment method is ready. Continue to checkout to finish
 * subscribing", which reads as if they had never subscribed.
 *
 * The backend now sends `accessState` (utils/subscriptionAccessState.js); this module is the
 * single place that turns it into title/message/CTA, so every page says the same thing.
 *
 * `action` tells the page which handler to call — pages keep their own navigation:
 *   "checkout"   -> subscription checkout
 *   "reactivate" -> POST /api/subscription-new/reactivate (canceling at period end)
 *   "add_card"   -> add/update payment method
 *   "none"       -> nothing to do
 */

const VIEWS = {
  never_started: {
    blocking: false,
    action: "none",
  },
  trialing: {
    blocking: false,
    action: "none",
  },
  active: {
    blocking: false,
    action: "none",
  },
  canceling_at_period_end: {
    blocking: true,
    tone: "warning",
    title: "Subscription canceled",
    ctaLabel: "Resume subscription",
    action: "reactivate",
  },
  grace: {
    blocking: true,
    tone: "warning",
    title: "Payment failed",
    ctaLabel: "Update payment method",
    action: "add_card",
  },
  past_due: {
    blocking: true,
    tone: "danger",
    title: "Payment failed",
    ctaLabel: "Update payment method",
    action: "add_card",
  },
  unpaid: {
    blocking: true,
    tone: "danger",
    title: "Subscription unpaid",
    ctaLabel: "Update payment method",
    action: "add_card",
  },
  incomplete: {
    blocking: true,
    tone: "warning",
    title: "Subscription setup incomplete",
    ctaLabel: "Continue to checkout",
    action: "checkout",
  },
  paused: {
    blocking: true,
    tone: "warning",
    title: "Subscription paused",
    ctaLabel: "Resubscribe",
    action: "checkout",
  },
  canceled: {
    blocking: true,
    tone: "danger",
    title: "Subscription canceled",
    ctaLabel: "Resubscribe",
    action: "checkout",
  },
  trial_ended: {
    blocking: true,
    tone: "danger",
    title: "Your trial has ended",
    ctaLabel: "Subscribe now",
    action: "checkout",
  },
};

const TONES = {
  danger: {
    background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
    accent: "#dc2626",
    shadow: "rgba(220, 38, 38, 0.3)",
  },
  warning: {
    background: "linear-gradient(135deg, #f59e0b 0%, #b45309 100%)",
    accent: "#b45309",
    shadow: "rgba(245, 158, 11, 0.3)",
  },
};

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getTime() <= 0) return null;
  return date.toLocaleDateString();
}

/**
 * Fallback for a response that predates `accessState` (or a cached one), so a page can never
 * regress to the old "anything non-active == trial ended" behaviour.
 */
function deriveStateFallback(subscription) {
  const status = String(subscription?.status || "").toLowerCase();
  if (subscription?.isInTrial) return "trialing";
  if (status === "canceled" || status === "cancelled") return "canceled";
  if (status === "unpaid") return "unpaid";
  if (status === "incomplete" || status === "incomplete_expired") {
    return "incomplete";
  }
  if (status === "paused") return "paused";
  if (status === "past_due") {
    const graceEnd = subscription?.gracePeriodEnd || subscription?.gracePeriodEndsAt;
    return graceEnd && new Date(graceEnd) > new Date() ? "grace" : "past_due";
  }
  if (status === "active" || status === "trialing") {
    const periodEnd = subscription?.subscriptionCurrentPeriodEnd || subscription?.currentPeriodEnd;
    if (
      subscription?.subscriptionCancelAtPeriodEnd &&
      periodEnd &&
      new Date(periodEnd) > new Date()
    ) {
      return "canceling_at_period_end";
    }
    return "active";
  }
  if (subscription?.trialStatus === "not_started") return "never_started";
  return "trial_ended";
}

/**
 * @param {object} subscription Response body from GET /api/subscription-new/status
 * @returns {{state:string, blocking:boolean, action:string, tone:string,
 *            title:string, message:string, ctaLabel:string, background:string, accent:string}}
 */
export function getSubscriptionAccessView(subscription) {
  const state = subscription?.accessState || deriveStateFallback(subscription);
  const view = VIEWS[state] || VIEWS.trial_ended;

  if (!view.blocking) {
    return {
      state,
      blocking: false,
      action: "none",
      tone: "none",
      title: "",
      message: "",
      ctaLabel: "",
      background: "",
      accent: "",
    };
  }

  const tone = TONES[view.tone] || TONES.danger;
  const periodEnd = formatDate(
    subscription?.subscriptionCurrentPeriodEnd || subscription?.currentPeriodEnd,
  );
  const canceledOn = formatDate(subscription?.subscriptionCanceledAt);
  const graceEnd = formatDate(
    subscription?.gracePeriodEnd || subscription?.gracePeriodEndsAt,
  );
  const monthlyTotal = subscription?.totalMonthly ?? subscription?.subscriptionAmount;

  let message;
  switch (state) {
    case "canceled":
      message = canceledOn
        ? `Your subscription was canceled on ${canceledOn}. Resubscribe to restore access.`
        : "Your subscription has been canceled. Resubscribe to restore access.";
      break;
    case "canceling_at_period_end":
      message = periodEnd
        ? `Your subscription is set to end on ${periodEnd}. Keep it to avoid losing access.`
        : "Your subscription is set to end at the close of the current period.";
      break;
    case "grace":
      message = graceEnd
        ? `We couldn't charge your payment method. Update it before ${graceEnd} to keep access.`
        : "We couldn't charge your payment method. Update it to keep access.";
      break;
    case "past_due":
      message =
        "We couldn't charge your payment method. Update it to restore access.";
      break;
    case "unpaid":
      message =
        "This subscription is unpaid. Add a working payment method to restore access.";
      break;
    case "incomplete":
      message = "Your subscription was never completed. Finish checkout to activate it.";
      break;
    case "paused":
      message = "Your subscription is paused. Resubscribe to restore access.";
      break;
    case "trial_ended":
    default:
      message = "Your trial has ended. Subscribe now to restore access.";
      break;
  }

  return {
    state,
    blocking: true,
    action: view.action,
    tone: view.tone,
    title: view.title,
    message,
    ctaLabel: view.ctaLabel,
    background: tone.background,
    accent: tone.accent,
    shadow: tone.shadow,
    monthlyTotal,
  };
}

export const SUBSCRIPTION_ACCESS_VIEWS = VIEWS;
